import { apiClient } from "@/api/api-client";
import { Button } from "@/components/core/buttons/button";
import { formatNumber } from "@/utils/format-number";
import { useQuery } from "@tanstack/react-query";
import type React from "react";
import { useState } from "react";

type BreakdownType = "recurring" | "contract_value" | "royalty" | "projects";

interface BreakdownRow {
	id: string;
	project_name: string | null;
	client_name: string | null;
	salesperson: { id: string; name: string } | null;
	payment_type: string | null;
	project_type: string | null;
	contract_amount: number;
	monthly_recurring_sales: number;
	monthly_recurring_equivalent: number;
	royalty_amount: number;
	contract_duration_months: number;
	start_date: string | null;
	end_date: string | null;
	status: string;
}

interface BreakdownResponse {
	type: BreakdownType;
	data: BreakdownRow[];
	meta: { current_page: number; last_page: number; per_page: number; total: number };
	total: number;
}

interface ContractsBreakdownModalProps {
	type: BreakdownType;
	dateFrom?: string;
	dateTo?: string;
	salespersonId?: string;
	status?: string;
}

const COPY: Record<BreakdownType, { heading: string; total: string; valueLabel: string; emoji: string }> = {
	recurring: {
		heading: "Sum of monthly recurring revenue (contract amount ÷ duration in months)",
		total: "Total Monthly Recurring",
		valueLabel: "Monthly Equivalent",
		emoji: "🔁",
	},
	contract_value: {
		heading: "Sum of contract_amount across non-royalty contracts",
		total: "Total Contract Value",
		valueLabel: "Contract Amount",
		emoji: "📄",
	},
	royalty: {
		heading: "Sum of royalty_amount across royalty projects",
		total: "Total Royalty Project",
		valueLabel: "Royalty Amount",
		emoji: "👑",
	},
	projects: {
		heading: "Projects whose start date falls within the selected range",
		total: "Total Projects",
		valueLabel: "Contract Amount",
		emoji: "📁",
	},
};

export const ContractsBreakdownModal: React.FC<ContractsBreakdownModalProps> = ({ type, dateFrom, dateTo, salespersonId, status }) => {
	const [page, setPage] = useState(1);

	const { data, isLoading } = useQuery<BreakdownResponse>({
		queryKey: ["sales-contracts-breakdown", type, dateFrom, dateTo, salespersonId, status, page],
		queryFn: async () => {
			const params: Record<string, string | number> = { type, page, per_page: 25 };
			if (dateFrom) params.date_from = dateFrom;
			if (dateTo) params.date_to = dateTo;
			if (salespersonId) params.salesperson_id = salespersonId;
			if (status) params.status = status;
			const response = await apiClient.get<BreakdownResponse>("/sales-contracts-breakdown", { params });
			return response.data;
		},
	});

	if (isLoading) {
		return (
			<div className="flex items-center justify-center py-10">
				<div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500" />
			</div>
		);
	}

	const rows = data?.data ?? [];
	const meta = data?.meta;
	const total = data?.total ?? 0;
	const copy = COPY[type];

	const paymentLabel = (pt: string | null) =>
		pt === "monthly" ? "Monthly" : pt === "quarterly" ? "Quarterly" : pt === "semi-annual" ? "Semi-Annual" : pt === "annually" ? "Annual" : "—";

	const valueFor = (row: BreakdownRow): number => {
		if (type === "recurring") return row.monthly_recurring_equivalent;
		if (type === "royalty") return row.royalty_amount;
		// contract_value and projects both display contract_amount
		return row.contract_amount;
	};

	const isCountTotal = type === "projects";

	return (
		<div className="space-y-4">
			<div className="bg-blue-50 border border-blue-200 rounded-lg p-3 flex items-center justify-between">
				<div>
					<p className="text-xs text-blue-700">{copy.heading}</p>
					<p className="text-xl font-bold text-blue-900">{isCountTotal ? `${total} project${total === 1 ? "" : "s"}` : `₱${formatNumber(total)}`}</p>
				</div>
				<p className="text-xs text-blue-700">
					{meta?.total ?? 0} contract{(meta?.total ?? 0) === 1 ? "" : "s"}
				</p>
			</div>

			{rows.length === 0 ? (
				<div className="text-center text-sm text-gray-500 py-8">No contracts in the selected range.</div>
			) : (
				<div className="overflow-x-auto border rounded-lg">
					<table className="w-full text-sm">
						<thead className="bg-gray-50 text-gray-700">
							<tr>
								<th className="text-left p-2">Project</th>
								<th className="text-left p-2">Client</th>
								<th className="text-left p-2">Salesperson</th>
								<th className="text-left p-2">Billing</th>
								{type === "projects" && <th className="text-left p-2">Start Date</th>}
								{type === "recurring" && (
									<>
										<th className="text-right p-2">Contract Amount</th>
										<th className="text-right p-2">Months</th>
									</>
								)}
								<th className="text-right p-2">{copy.valueLabel}</th>
							</tr>
						</thead>
						<tbody>
							{rows.map((row) => (
								<tr key={row.id} className="border-t hover:bg-gray-50">
									<td className="p-2 text-gray-900">{row.project_name ?? "—"}</td>
									<td className="p-2 text-gray-700">{row.client_name ?? "—"}</td>
									<td className="p-2 text-gray-700">{row.salesperson?.name ?? "—"}</td>
									<td className="p-2 text-gray-700">{paymentLabel(row.payment_type)}</td>
									{type === "projects" && (
										<td className="p-2 text-gray-700 tabular-nums">{row.start_date ? new Date(row.start_date).toLocaleDateString() : "—"}</td>
									)}
									{type === "recurring" && (
										<>
											<td className="p-2 text-right tabular-nums">₱{formatNumber(row.contract_amount)}</td>
											<td className="p-2 text-right tabular-nums">{row.contract_duration_months}</td>
										</>
									)}
									<td className="p-2 text-right tabular-nums font-semibold text-blue-700">₱{formatNumber(valueFor(row))}</td>
								</tr>
							))}
						</tbody>
					</table>
				</div>
			)}

			{meta && meta.last_page > 1 && (
				<div className="flex items-center justify-between pt-2">
					<p className="text-xs text-gray-600">
						Page {meta.current_page} of {meta.last_page}
					</p>
					<div className="flex gap-2">
						<Button variant="outline" size="sm" disabled={meta.current_page <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))}>
							Previous
						</Button>
						<Button variant="outline" size="sm" disabled={meta.current_page >= meta.last_page} onClick={() => setPage((p) => p + 1)}>
							Next
						</Button>
					</div>
				</div>
			)}
		</div>
	);
};

// Back-compat re-export so existing imports continue to work.
export const RecurringContractsModal: React.FC<Omit<ContractsBreakdownModalProps, "type">> = (props) => <ContractsBreakdownModal type="recurring" {...props} />;
